import React, { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Search, Plus, X, ChevronDown, Link2, ArrowUpRight, FileText, Loader2 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { API, LINKABLE_ENTITY_TYPES } from './calendarConfig';

// ============================================================================
// PLATFORM LINKING COMPONENT
// ============================================================================
export const PlatformLinkingSection = ({ linkedEntities, onAddLink, onRemoveLink, onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [isOpen, setIsOpen] = useState(false);
  const searchTimeout = useRef(null);

  // Search for linkable entities
  const searchEntities = useCallback(async (query, entityTypes = 'all') => {
    if (!query.trim() && entityTypes === 'all') {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/calendar/search-linkable`, {
        params: { 
          query: query.trim(),
          entity_types: entityTypes,
          limit: 20
        }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching entities:', error);
      toast.error('Failed to search entities');
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      const types = activeTab === 'all' ? 'all' : activeTab;
      searchEntities(value, types);
    }, 300);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (searchQuery.trim() || tab !== 'all') {
      searchEntities(searchQuery, tab === 'all' ? 'all' : tab);
    }
  };

  // Get all linked entities as flat array
  const getAllLinkedEntities = () => {
    const entities = [];
    Object.entries(LINKABLE_ENTITY_TYPES).forEach(([type, config]) => {
      const ids = linkedEntities[config.fieldMultiple] || [];
      const names = linkedEntities[config.nameMultiple] || [];
      ids.forEach((id, idx) => {
        entities.push({
          id,
          type,
          name: names[idx]?.name || id,
          config
        });
      });
    });
    return entities;
  };

  const allLinked = getAllLinkedEntities();
  const linkedCount = allLinked.length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-600" />
            <div>
              <Label className="text-sm font-medium cursor-pointer">Link to Platform</Label>
              <p className="text-xs text-muted-foreground">Connect to Jobs, Contacts, EOS, etc.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {linkedCount > 0 && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                {linkedCount} linked
              </Badge>
            )}
            <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
          </div>
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-2 space-y-3">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search Jobs, Contacts, EOS items..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 pr-9"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid grid-cols-6 h-8 p-0.5">
            <TabsTrigger value="all" className="text-xs px-1">All</TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs px-1">Jobs</TabsTrigger>
            <TabsTrigger value="contacts" className="text-xs px-1">Contacts</TabsTrigger>
            <TabsTrigger value="leads" className="text-xs px-1">Leads</TabsTrigger>
            <TabsTrigger value="eos_meetings,eos_rocks,eos_todos,eos_issues" className="text-xs px-1">EOS</TabsTrigger>
            <TabsTrigger value="calls,chats,communications" className="text-xs px-1">Comms</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <ScrollArea className="h-40 border rounded-lg">
            <div className="p-2 space-y-1">
              {searchResults.map((result) => {
                const config = LINKABLE_ENTITY_TYPES[result.type];
                const Icon = config?.icon || FileText;
                const isLinked = (linkedEntities[config?.fieldMultiple] || []).includes(result.id);
                
                return (
                  <div
                    key={`${result.type}-${result.id}`}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors",
                      isLinked ? "bg-blue-50 border border-blue-200" : "hover:bg-muted"
                    )}
                    onClick={() => !isLinked && onAddLink(result.type, result.id, result.name)}
                  >
                    <div 
                      className={cn("p-1.5 rounded", config?.bg || 'bg-gray-100')}
                      style={{ color: config?.color }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                    </div>
                    {isLinked ? (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">Linked</Badge>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 px-2">
                        <Plus className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}

        {/* Empty state when searching */}
        {searchQuery && searchResults.length === 0 && !isSearching && (
          <div className="text-center py-4 text-sm text-muted-foreground">
            No results found for &quot;{searchQuery}&quot;
          </div>
        )}

        {/* Currently Linked Entities */}
        {allLinked.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Linked Items</Label>
            <div className="flex flex-wrap gap-2">
              {allLinked.map((entity) => {
                const Icon = entity.config.icon;
                return (
                  <Badge
                    key={`${entity.type}-${entity.id}`}
                    variant="secondary"
                    className={cn("gap-1.5 pr-1 cursor-pointer group", entity.config.bg)}
                    style={{ color: entity.config.color }}
                  >
                    <Icon className="h-3 w-3" />
                    <span 
                      className="max-w-[120px] truncate hover:underline"
                      onClick={() => onNavigate(entity.type, entity.id)}
                    >
                      {entity.name}
                    </span>
                    <X
                      className="h-3 w-3 ml-1 opacity-50 hover:opacity-100 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveLink(entity.type, entity.id);
                      }}
                    />
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Add Buttons */}
        {!searchQuery && searchResults.length === 0 && (
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(LINKABLE_ENTITY_TYPES).slice(0, 8).map(([type, config]) => {
              const Icon = config.icon;
              const count = (linkedEntities[config.fieldMultiple] || []).length;
              return (
                <Button
                  key={type}
                  variant="outline"
                  size="sm"
                  className="h-auto py-2 px-2 flex-col gap-1"
                  onClick={() => {
                    setActiveTab(type.includes('eos') ? 'eos_meetings,eos_rocks,eos_todos,eos_issues' : type + 's');
                    searchEntities('', type + 's');
                  }}
                >
                  <Icon className="h-4 w-4" style={{ color: config.color }} />
                  <span className="text-xs truncate w-full">{config.label.split(' ')[0]}</span>
                  {count > 0 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0">{count}</Badge>
                  )}
                </Button>
              );
            })}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

// ============================================================================
// LINKED ENTITIES DISPLAY COMPONENT (for event detail view)
// ============================================================================
export const LinkedEntitiesDisplay = ({ event, onNavigate }) => {
  const allLinked = [];
  
  Object.entries(LINKABLE_ENTITY_TYPES).forEach(([type, config]) => {
    const names = event[config.nameMultiple] || [];
    names.forEach((item) => {
      allLinked.push({
        id: item.id,
        type,
        name: item.name,
        config
      });
    });
    
    // Also check single fields for backward compatibility
    if (event[config.fieldSingle] && event[config.nameSingle]) {
      const exists = allLinked.some(e => e.id === event[config.fieldSingle]);
      if (!exists) {
        allLinked.push({
          id: event[config.fieldSingle],
          type,
          name: event[config.nameSingle],
          config
        });
      }
    }
  });

  if (allLinked.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wide">
        <Link2 className="h-3 w-3" />
        Linked Items
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allLinked.map((entity) => {
          const Icon = entity.config.icon;
          return (
            <Badge
              key={`${entity.type}-${entity.id}`}
              variant="secondary"
              className={cn("gap-1 cursor-pointer hover:opacity-80 transition-opacity", entity.config.bg)}
              style={{ color: entity.config.color }}
              onClick={() => onNavigate(entity.type, entity.id)}
            >
              <Icon className="h-3 w-3" />
              <span className="max-w-[100px] truncate">{entity.name}</span>
              <ArrowUpRight className="h-3 w-3 opacity-50" />
            </Badge>
          );
        })}
      </div>
    </div>
  );
};
